import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage, SUPPORTED_LANGUAGES } from '../contexts/LanguageContext';

const MarketingHeader = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
    const [showFeaturesDropdown, setShowFeaturesDropdown] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedbackData, setFeedbackData] = useState({ category: 'General', rating: 5, comments: '' });
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [feedbackSuccess, setFeedbackSuccess] = useState(false);
    
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const dropdownRef = useRef(null);
    const headerWrapperRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(120);

    const [bannerConfig, setBannerConfig] = useState({ is_active: false, banner_text: '' });

    const showBanner = bannerConfig.is_active;

    const fetchBanner = async () => {
        try {
            const API_URL = process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com';
            const res = await fetch(`${API_URL}/api/public/announcement-banner`);
            if (res.ok) {
                const json = await res.json();
                if (json.success && json.data) {
                    setBannerConfig(json.data);
                }
            }
        } catch (err) {
            console.error('Failed to fetch banner config:', err);
        }
    };

    useEffect(() => {
        fetchBanner();
        window.addEventListener('focus', fetchBanner);
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') fetchBanner();
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            window.removeEventListener('focus', fetchBanner);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, []);

    useEffect(() => {
        const updateHeight = () => {
            if (headerWrapperRef.current) {
                setHeaderHeight(headerWrapperRef.current.offsetHeight);
            }
        };
        
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [showBanner]);


    // BENTO GRID FEATURES for dropdown
    const features = [
        {
            id: 'qr',
            title: t('qrScanTitle') || 'QR Ordering',
            description: t('qrScanDesc') || 'Instant scanning and ordering',
            color: '#FF6B6B',
            icon: 'fa-qrcode',
            featureId: 'qr-ordering'
        },
        {
            id: 'kds',
            title: t('liveKDS') || 'Kitchen Display',
            description: t('kdsDesc') || 'Smart kitchen order management',
            color: '#FFA502',
            icon: 'fa-fire',
            featureId: 'kds'
        },
        {
            id: 'analytics',
            title: t('analyticsInsights') || 'Analytics',
            description: t('analyticsInsightsDesc') || 'Data-driven business decisions',
            color: '#2ED573',
            icon: 'fa-chart-line',
            featureId: 'analytics'
        },
        {
            id: 'tables',
            title: t('tableMgmt') || 'Table Management',
            description: t('tableMgmtDesc') || 'Digital floor plan & seating',
            color: '#A3CB38',
            icon: 'fa-th',
            featureId: 'table-management'
        },
        {
            id: 'menu',
            title: t('dynamicMenu') || 'Menu Management',
            description: t('dynamicMenuDesc') || 'Update prices & items instantly',
            color: '#1289A7',
            icon: 'fa-book-open',
            featureId: 'menu-management'
        },
        {
            id: 'team',
            title: t('roleMgmt') || 'Staff Roles',
            description: t('roleMgmtDesc') || 'Secure access control',
            color: '#5352ED',
            icon: 'fa-user-shield',
            featureId: 'role-management'
        },
        {
            id: 'branches',
            title: t('branchMgmt') || 'Multi-Branch',
            description: t('branchMgmtDesc') || 'Manage all locations together',
            color: '#FF6348',
            icon: 'fa-code-branch',
            featureId: 'branch-management'
        },
        {
            id: 'inventory',
            title: t('smartInventory') || 'Inventory Tracking',
            description: t('inventoryDesc') || 'Real-time stock monitoring',
            color: '#1E90FF',
            icon: 'fa-cubes',
            featureId: 'inventory'
        },
        {
            id: 'loyalty-rewards',
            title: t('loyaltyRewardsTitle') || 'Loyalty & Rewards',
            description: t('loyaltyRewardsDesc') || 'Points rewards and birthday discount settings',
            color: '#14B8A6',
            icon: 'fa-gift',
            featureId: 'loyalty-rewards'
        },
        {
            id: 'manual-ordering',
            title: t('manualOrderingTitle') || 'Manual Ordering',
            description: t('manualOrderingDesc') || 'Log Swiggy, Zomato & phone orders directly',
            color: '#10B981',
            icon: 'fa-keyboard',
            featureId: 'manual-ordering'
        }
    ];

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowLanguageDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const goToSignup = () => {
        setIsLoading(true);
        setTimeout(() => navigate('/signup'), 500);
    };

    const handleFeedbackSubmit = async (e) => {
        e.preventDefault();
        setIsSubmittingFeedback(true);
        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const res = await fetch(`${API_URL}/api/platform/feedbacks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(feedbackData)
            });
            if (res.ok) {
                setFeedbackSuccess(true);
                setFeedbackData({ category: 'General', rating: 5, comments: '' });
                setTimeout(() => {
                    setShowFeedbackModal(false);
                    setFeedbackSuccess(false);
                }, 4000);
            } else {
                alert('Failed to submit feedback. Please try again later.');
            }
        } catch (error) {
            console.error(error);
            alert('An error occurred.');
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    return (
        <>
        {showFeedbackModal && (
            <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                <div className="bg-[#0f172a] border border-white/10 rounded-2xl w-full max-w-md p-6 relative shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                    <button onClick={() => { setShowFeedbackModal(false); setFeedbackSuccess(false); }} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
                        <i className="fas fa-times text-xl"></i>
                    </button>
                    {feedbackSuccess ? (
                        <div className="flex flex-col items-center justify-center py-10 text-center animate-in zoom-in duration-300">
                            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                                <i className="fas fa-check text-3xl text-green-400"></i>
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Thank you for your feedback!</h3>
                            <p className="text-gray-400">We appreciate your input.</p>
                        </div>
                    ) : (
                        <>
                            <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                                    <i className="fas fa-comment-dots"></i>
                                </div>
                                We Value Your Feedback
                            </h3>
                            <p className="text-gray-400 text-sm mb-6">Help us improve RedSorm by sharing your thoughts.</p>
                    
                            <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-1">Category</label>
                                    <div className="relative">
                                        <i className="fas fa-tag absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                                        <select 
                                            className="w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-9 pr-4 text-white focus:outline-none focus:border-indigo-500 appearance-none"
                                            value={feedbackData.category}
                                            onChange={(e) => setFeedbackData({...feedbackData, category: e.target.value})}
                                        >
                                            <option value="General" className="bg-slate-800">General Feedback</option>
                                            <option value="Feature Request" className="bg-slate-800">Feature Request</option>
                                            <option value="Bug Report" className="bg-slate-800">Bug Report</option>
                                            <option value="Pricing" className="bg-slate-800">Pricing / Plans</option>
                                        </select>
                                        <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-1">Rating</label>
                                    <div className="flex gap-2">
                                        {[1,2,3,4,5].map(star => (
                                            <button 
                                                type="button" 
                                                key={star}
                                                onClick={() => setFeedbackData({...feedbackData, rating: star})}
                                                className={`text-2xl transition-all ${feedbackData.rating >= star ? 'text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]' : 'text-gray-600 hover:text-amber-400/50'}`}
                                            >
                                                <i className="fas fa-star"></i>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-1">Comments (Optional)</label>
                                    <textarea 
                                        className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white focus:outline-none focus:border-indigo-500 resize-none h-24"
                                        placeholder="Tell us more about your experience..."
                                        value={feedbackData.comments}
                                        onChange={(e) => setFeedbackData({...feedbackData, comments: e.target.value})}
                                    ></textarea>
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={isSubmittingFeedback}
                                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white font-bold py-3 rounded-xl transition-all shadow-[0_0_15px_rgba(99,102,241,0.3)] hover:shadow-[0_0_25px_rgba(99,102,241,0.5)] disabled:opacity-50 mt-2"
                                >
                                    {isSubmittingFeedback ? <><i className="fas fa-spinner fa-spin mr-2"></i> Submitting...</> : 'Submit Feedback'}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        )}

        <div className="marketing-header-wrapper" ref={headerWrapperRef}>
            {showBanner && (
                <div className="promo-banner-premium" style={{
                    background: 'linear-gradient(135deg, #dc2626 0%, #ea580c 40%, #eab308 100%)',
                    backgroundSize: '200% 200%',
                    animation: 'promoGradientShift 6s ease infinite',
                    color: '#ffffff',
                    padding: '12px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: '12px',
                    position: 'relative',
                    width: '100%',
                    boxShadow: '0 4px 20px rgba(234, 88, 12, 0.25), inset 0 -1px 0 rgba(255, 255, 255, 0.1)',
                    fontFamily: "'Outfit', 'Inter', system-ui, sans-serif"
                }}>
                    <style>{`
                        @keyframes promoGradientShift {
                            0% { background-position: 0% 50% }
                            50% { background-position: 100% 50% }
                            100% { background-position: 0% 50% }
                        }
                        @keyframes badgePulse {
                            0% { transform: scale(1) }
                            50% { transform: scale(1.05) }
                            100% { transform: scale(1) }
                        }
                        @keyframes buttonGlowPulse {
                            0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4) }
                            70% { box-shadow: 0 0 0 8px rgba(255, 255, 255, 0) }
                            100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0) }
                        }
                    `}</style>

                    <div style={{
                        background: 'rgba(255, 255, 255, 0.15)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        padding: '4px 10px',
                        borderRadius: '9999px',
                        fontSize: '0.72rem',
                        fontWeight: 900,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        animation: 'badgePulse 2s infinite ease-in-out',
                        backdropFilter: 'blur(4px)'
                    }}>
                        <span style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#22c55e',
                            boxShadow: '0 0 8px #22c55e'
                        }}></span>
                        Flash Sale
                    </div>

                    <div style={{
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        letterSpacing: '-0.01em',
                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        flexWrap: 'wrap',
                        justifyContent: 'center'
                    }}>
                        <span>{bannerConfig.banner_text}</span>
                    </div>

                    <button
                        onClick={() => navigate('/pricing')}
                        className="promo-claim-btn"
                        style={{
                            background: '#ffffff',
                            color: '#ea580c',
                            padding: '8px 18px',
                            borderRadius: '9999px',
                            fontSize: '0.78rem',
                            fontWeight: 900,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            border: 'none',
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.2s ease',
                            animation: 'buttonGlowPulse 2s infinite',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px) scale(1.03)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'none';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                        }}
                    >
                        Claim 90% Off <i className="fas fa-arrow-right" style={{ fontSize: '0.72rem' }}></i>
                    </button>
                </div>
            )}
            <header className={`header ${scrolled ? 'scrolled' : ''}`}>
            <div className="container">
                <div className="header-content">
                    <div className="logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
                        <div style={{
                            width: scrolled ? '40px' : '48px',
                            height: scrolled ? '40px' : '48px',
                            borderRadius: '50%',
                            background: '#fff',
                            border: '2px solid rgba(16, 185, 129, 0.15)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            transition: 'all 0.3s ease',
                            padding: '4px',
                        }}>
                            <img
                                src="/app-logo.png"
                                alt="RedSorm"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        </div>
                        <span className="logo-text" style={{
                            fontWeight: 800,
                            fontSize: scrolled ? '1rem' : '1.15rem',
                            letterSpacing: '-0.02em',
                            transition: 'all 0.3s ease',
                        }}>RedSorm</span>
                    </div>
                    <nav className={`nav ${mobileMenuOpen ? 'open' : ''}`}>
                        <ul className="nav-list">
                            <li><a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); setMobileMenuOpen(false); }}>{t('home')}</a></li>
                            <li 
                                className="nav-item-with-dropdown"
                                onMouseEnter={() => setShowFeaturesDropdown(true)}
                                onMouseLeave={() => setShowFeaturesDropdown(false)}
                            >
                                <a 
                                    href="/features" 
                                    onClick={(e) => { 
                                        e.preventDefault(); 
                                        navigate('/features');
                                        setMobileMenuOpen(false);
                                    }}
                                >
                                    {t('features')} <i className="fas fa-chevron-down ml-1" style={{ fontSize: '0.7rem' }}></i>
                                </a>
                                {showFeaturesDropdown && (
                                    <div className="features-dropdown">
                                        <div className="features-dropdown-grid">
                                            {features.map((feature) => (
                                                <div
                                                    key={feature.id}
                                                    className="feature-dropdown-item"
                                                    onClick={() => {
                                                        navigate(`/features?feature=${feature.featureId}`);
                                                        setShowFeaturesDropdown(false);
                                                        setMobileMenuOpen(false);
                                                    }}
                                                >
                                                    <div className="feature-dropdown-icon" style={{ backgroundColor: feature.color }}>
                                                        <i className={`fas ${feature.icon}`}></i>
                                                    </div>
                                                    <div className="feature-dropdown-content">
                                                        <h4>{feature.title}</h4>
                                                        <p>{feature.description}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </li>
                            <li><a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); setMobileMenuOpen(false); }}>{t('pricing')}</a></li>
                            <li><a href="/faqs" onClick={(e) => { e.preventDefault(); navigate('/faqs'); setMobileMenuOpen(false); }}>FAQs</a></li>
                            <li><button onClick={() => { setShowFeedbackModal(true); setMobileMenuOpen(false); }} className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1 font-semibold"><i className="fas fa-comment-dots text-xs"></i> Feedback</button></li>
                            <li className="mobile-only mt-6 w-full px-4">
                                <div className="flex flex-col gap-3">
                                    <button className="btn btn-secondary w-full" onClick={() => { navigate('/signup?mode=login'); setMobileMenuOpen(false); }}>{t('login')}</button>
                                    <button className="btn btn-primary w-full glow-effect" onClick={() => { goToSignup(); setMobileMenuOpen(false); }} disabled={isLoading}>
                                        {isLoading ? <><i className="fas fa-spinner fa-spin mr-2"></i> Loading...</> : t('getStarted')}
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </nav>
                    <div className="header-actions">
                        <div className="relative inline-block text-left mr-2" ref={dropdownRef}>
                            <button onClick={() => setShowLanguageDropdown(!showLanguageDropdown)} className="btn btn-ghost flex items-center gap-2">
                                <i className="fas fa-globe"></i> {language.toUpperCase()}
                            </button>
                            {showLanguageDropdown && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 py-1">
                                    <div className="px-4 py-2 border-b border-gray-100 flex justify-between items-center">
                                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Language</span>
                                        <span className="text-[8px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-black uppercase tracking-wider">Soon</span>
                                    </div>
                                    {SUPPORTED_LANGUAGES.map((lang) => (
                                        <button key={lang.code} onClick={() => { 
                                            if (language === lang.code) {
                                                setShowLanguageDropdown(false); 
                                            } else {
                                                alert('This language is coming soon!');
                                            }
                                        }} className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between ${language === lang.code ? 'font-bold bg-emerald-50 text-emerald-600 cursor-pointer' : 'text-gray-400 opacity-70 cursor-not-allowed hover:bg-gray-50'}`}>
                                            <span>{lang.label}</span>
                                            {language === lang.code && <i className="fas fa-check text-emerald-600 text-xs"></i>}
                                            {language !== lang.code && <i className="fas fa-clock text-amber-500 text-[10px]"></i>}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button className="btn btn-secondary" onClick={() => navigate('/signup?mode=login')}>{t('login')}</button>
                        <button className="btn btn-primary glow-effect" onClick={goToSignup} disabled={isLoading}>
                            {isLoading ? <><i className="fas fa-spinner fa-spin mr-2"></i> Loading...</> : t('getStarted')}
                        </button>
                        <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                            <span className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}><span></span><span></span><span></span></span>
                        </button>
                    </div>
                </div>
            </div>
        </header>
        </div>
        <div className="marketing-header-spacer" aria-hidden="true" style={{ height: `${headerHeight}px`, width: '100%', display: 'block' }}></div>
        </>
    );
};

export default MarketingHeader;
